import { Button, IconFont, Image, Popover, Text, Tooltip, useDisclosure } from '@byecode/ui'
import { autoUpdate, flip, limitShift, offset, shift, useFloating } from '@floating-ui/react'
import { getAssetUrl } from '@lighthouse/assets'
import { TagIcon, useAtomAction, useAtomData } from '@lighthouse/shared'
import { mergeRefs } from '@lighthouse/tools'
import { useLocation } from 'react-router'
import { useToggle } from 'react-use'
import styled from 'styled-components'

import { isShowTaskQuickStackAtom } from '@/atoms/application/state'
import { useCurrentAppId } from '@/hooks/useApplication'
import { useUser } from '@/hooks/useUser'

import { GuideTask } from './GuideTask'

const FloatButton = styled.button`
    appearance: none;
    -webkit-tap-highlight-color: transparent;
    position: fixed;
    z-index: 10;
    right: 20px;
    bottom: 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    padding: 8px;
    color: var(--color-white);
    background-color: var(--color-black);
`

const OptionItem = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 8px 16px;
    height: 36px;
    font-size: 14px;
    line-height: 22px;
    color: var(--color-gray-900);
    cursor: pointer;
    user-select: none;
    &:hover {
        background-color: var(--color-gray-100);
    }
`

const CloseIcon = styled(IconFont)`
    padding: 6px;
    border-radius: 50%;
    cursor: pointer;
    background-color: var(--color-gray-100);
    &:hover {
        background-color: var(--color-gray-200);
    }
`

const PopoverContainer = styled.div`
    z-index: 200;
    background: #fff;
    padding: 8px 0;
    border-radius: 8px;
    border: 1px solid var(--color-gray-200);
    box-shadow: 0px 4px 12px rgba(16, 24, 40, 0.1);
`

const SCxCloseIcon = styled(TagIcon)`
    position: absolute;
    right: -9px;
    top: -9px;
    display: none;
`
const SCxQuickStartButton = styled(Button)`
    &:hover {
        ${SCxCloseIcon} {
            display: flex;
        }
    }
`

export const Feedback = () => {
    const [optionOpen, toggleOptionOpen] = useToggle(false)
    const [formOpen, toggleFormOpen] = useToggle(false)
    const [guideOpen, { toggle: toggleGuideOpen }] = useDisclosure(false)
    const user = useUser()
    const appId = useCurrentAppId()
    const isShowTaskQuickStack = useAtomData(isShowTaskQuickStackAtom)

    const { run: setIsShowTaskQuickStack } = useAtomAction(isShowTaskQuickStackAtom)

    const location = useLocation()
    const isShowAppGuideBtn = user.userId && location.pathname.split('/')?.[1].includes(appId) && !!appId
    const isShowQuickTaskAppGuide = isShowAppGuideBtn && isShowTaskQuickStack

    const optionFloating = useFloating({
        strategy: 'fixed',
        placement: 'top-end',
        middleware: [flip({ fallbackPlacements: ['left-start'] }), offset(8), shift({ limiter: limitShift() })],
        whileElementsMounted: autoUpdate
    })

    const formFloating = useFloating({
        strategy: 'fixed',
        placement: 'top-end',
        middleware: [flip({ fallbackPlacements: ['left-start'] }), offset(8), shift({ limiter: limitShift() })],
        whileElementsMounted: autoUpdate
    })

    const guideFloating = useFloating({
        open: guideOpen,
        onOpenChange: toggleGuideOpen,
        strategy: 'fixed',
        placement: 'top-end',
        middleware: [offset(8), shift({ limiter: limitShift() })],
        whileElementsMounted: autoUpdate
    })

    return (
        <>
            <Tooltip
                title="帮助与资源"
                id="byecode-help"
                placement="top-end"
                ref={mergeRefs([optionFloating.refs.setReference, formFloating.refs.setReference, guideFloating.refs.setReference])}
            >
                <FloatButton
                    onClick={() => {
                        if (formOpen) {
                            toggleFormOpen()
                        } else {
                            toggleOptionOpen()
                        }
                    }}
                >
                    <IconFont type="QuestionMark" size={28} />
                </FloatButton>
            </Tooltip>

            {optionOpen && (
                <PopoverContainer style={{ width: 180, ...optionFloating.floatingStyles }} ref={optionFloating.refs.setFloating}>
                    <Popover width={200} trigger="hover" position="left">
                        <Popover.Target>
                            <OptionItem>
                                <IconFont type="Service" size={16} fill="var(--color-gray-500)" />
                                <Text>联系客服</Text>
                            </OptionItem>
                        </Popover.Target>
                        <Popover.Dropdown style={{ padding: 0, overflow: 'hidden', border: 'none' }}>
                            <Image width={200} height={200} src={getAssetUrl('application', 'company-contact.png')} />
                        </Popover.Dropdown>
                    </Popover>
                    <OptionItem
                        onClick={() => {
                            toggleOptionOpen()
                        }}
                        as="a"
                        href="https://www.byecode.com/cooperate"
                        target="_blank"
                    >
                        <IconFont type="Hearts" size={16} fill="var(--color-gray-500)" />
                        <Text>渠道合作</Text>
                    </OptionItem>
                    <OptionItem
                        onClick={() => {
                            toggleOptionOpen()
                        }}
                        as="a"
                        href="https://university.byecode.site/TdcFxAqcYo59"
                        target="_blank"
                    >
                        <IconFont type="SquareAcademicCap" size={16} fill="var(--color-gray-500)" />
                        <Text>ByeCode 学院</Text>
                    </OptionItem>
                    <OptionItem
                        onClick={() => {
                            toggleOptionOpen()
                        }}
                        as="a"
                        href="https://byecodehelp.yuque.com/org-wiki-byecodehelp-zavfcl/oevich?#"
                        target="_blank"
                    >
                        <IconFont type="QuestionCircle" size={16} fill="var(--color-gray-500)" />
                        <Text>帮助中心</Text>
                    </OptionItem>
                    <OptionItem
                        onClick={() => {
                            toggleOptionOpen()
                        }}
                        as="a"
                        href="https://byecodehelp.yuque.com/org-wiki-byecodehelp-zavfcl/oevich/lcrl81ikyzfei1gz?singleDoc#"
                        target="_blank"
                    >
                        <IconFont type="Keyboard" size={16} fill="var(--color-gray-500)" />
                        <Text>快捷键</Text>
                    </OptionItem>

                    {!isShowQuickTaskAppGuide && (
                        <OptionItem
                            onClick={() => {
                                toggleOptionOpen()
                                toggleGuideOpen()
                                setIsShowTaskQuickStack(true)
                            }}
                        >
                            <IconFont type="Publish" size={16} fill="var(--color-gray-500)" />
                            <Text>快速开始</Text>
                        </OptionItem>
                    )}
                </PopoverContainer>
            )}
            {isShowQuickTaskAppGuide && (
                <SCxQuickStartButton
                    type="primary"
                    style={{ position: 'fixed', right: 72, bottom: 26, height: 40, zIndex: 10 }}
                    icon={<IconFont type="Publish" size={16} color="var(--color-white)" />}
                    onClick={() => toggleGuideOpen()}
                >
                    <Text>快速开始</Text>
                    <IconFont type="ArrowUpSmall" size={16} color="var(--color-white)" />
                    <SCxCloseIcon
                        icon="Close"
                        size={18}
                        iconSize={12}
                        background="var(--color-gray-400)"
                        radius={100}
                        enableHover
                        hoverBackground="var(--color-black)"
                        onClick={e => {
                            e.stopPropagation()
                            setIsShowTaskQuickStack(false)
                        }}
                    />
                </SCxQuickStartButton>
            )}
            {guideOpen && isShowAppGuideBtn && (
                <PopoverContainer style={{ width: 400, ...formFloating.floatingStyles, padding: 0 }} ref={formFloating.refs.setFloating}>
                    <GuideTask onClose={() => toggleGuideOpen()} />
                </PopoverContainer>
            )}
        </>
    )
}
